<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      ref="tableRemoteRef"
      :headers="tableHeaders"
      :items="displayedFeatures"
      :pagination="pagination"
      :isSearchable="false"
      :isSelectable="false"
      :itemActions="actions"
      :isCustomIcon="true"
      :title="$t('features.pageName')"
      hideDefaultFooter
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :withoutFilter="true"
          :searchPlaceholder="$t('features.searchBy')"
          :filterName="filterName"
          @onSearch="handleSearch"
        />
      </template>
      <template v-slot:item.price="{ item }">
        <span>{{ item.price !== null && item.price !== undefined ? `${item.price} $` : '' }}</span>
      </template>
      <template v-slot:item.description="{ item }">
        <span>{{ item.description ? (item.description[$i18n.locale] || item.description[teamMainLang] || '') : '' }}</span>
      </template>
      <template v-slot:item.active="{ item }">
        <v-switch
          readonly
          :inputValue="item.active"
          @click.stop="handleToggleClick(item)"
        />
      </template>
      <template v-slot:item.createdAt="{ item }">
        <span>{{ formatDate(item.createdAt) }}</span>
      </template>
      <template v-slot:item.updatedAt="{ item }">
        <span>{{ formatDate(item.updatedAt) }}</span>
      </template>
      <template v-slot:item.activationDate="{ item }">
        <span>{{ formatDate(item.activationDate) }}</span>
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import FeatureActivationForm from '@/components/forms/FeatureActivationForm.vue';

export default {
    name: 'Features',
    components: {
        TableRemote,
        SearchWithFilters,
    },
    data() {
        return {
            isInitializing: false,
            allHeadersTitles: [
                'key',
                'type',
                'description',
                'price',
                'createdAt',
                'activationDate',
                'endAt',
                'activatedBy',
                'active',
            ],
            items: [],
            team: {},
            pagination: {},
            queryParams: {},
            filterName: 'mfeatures',
        };
    },
    computed: {
        ...mapGetters([
            'teamID',
            'teamMainLang',
        ]),

        tableHeaders() {
            const headers = this.allHeadersTitles.map(header => ({
                text: this.$t(`mfeatures.headers.${header}`),
                value: header,
                sortable: header !== 'accessGroups',
            }));
            headers.unshift({
                text: '',
                value: 'icon',
                sortable: false,
            });
            return headers;
        },
        actions() {
            const actions = [
                {
                    name: this.$t('mfeatures.api'),
                    icon: 'settings',
                    mode: 'edit',
                },
            ];
            return actions;
        },
        displayedFeatures() {
            return this.items.map(item => {
                const activeFeature = this.team?.activeFeatures?.find(
                    // eslint-disable-next-line no-underscore-dangle
                    f => f?.feature?._id?.toString() === item.id.toString(),
                );
                const activatedByProfile = activeFeature?.activatedBy?.profile;

                return {
                    ...item,
                    active: !!activeFeature,
                    activationDate: activeFeature?.activatedAt ?? null,
                    activatedBy: activatedByProfile
                        ? `${activatedByProfile.firstName} ${activatedByProfile.lastName}`
                        : null,
                };
            });
        },
    },
    async created() {
        this.isInitializing = true;
        await this.loadFeatures();
        await this.loadTeam();
        this.isInitializing = false;
    },
    methods: {
        async loadFeatures() {
            try {
                const features = await this.$rq.getFeatures(this.queryParams);
                this.items = features.data;
            } catch (error) {
                this.$toasted.global.toast_error({
                    message: this.$t('features.msg.loadError'),
                });
            }
        },
        async loadTeam() {
            try {
                const team = await this.$rq.getTeam(this.teamID);
                this.team = team.data;
            } catch (error) {
                this.$toasted.global.toast_error({
                    message: this.$t('features.msg.loadError'),
                });
            }
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.items = [];
            this.loadFeatures();
        },
        handleToggleClick(item) {
            this.featureToToggle = item.key;
            this.newActiveState = !item.active;
            const { title, message, additionalMessage } = this.getConfirmationMessages(this.newActiveState);
            this.$root.$emit('showModal', {
                title,
                component: Vue.component('ConfirmForm', FeatureActivationForm),
                payload: {
                    msg: message,
                    additionalMsg: additionalMessage,
                },
                onConfirm: () => this.confirmToggle(),
                isForm: false,
            });
        },
        getConfirmationMessages(isActivating) {
            return {
                title: isActivating ? this.$t('features.confirmActivateTitle') : this.$t('features.confirmDeactivateTitle'),
                message: isActivating ? this.$t('features.confirmActivateMessage') : this.$t('features.confirmDeactivateMessage'),
                additionalMessage: isActivating
                    ? this.$t('features.confirmActivateAdditionalMessage')
                    : this.$t('features.confirmDeactivateAdditionalMessage'),
            };
        },
        async confirmToggle() {
            try {
                const featureKey = this.featureToToggle;
                const featureToToggle = this.items.find(
                    item => item.key === featureKey,
                );
                await this.toggleFeatureActive(featureToToggle, this.newActiveState);
                this.featureToToggle = null;
                this.$toasted.global.toast_success({
                    message: this.newActiveState
                        ? this.$t('features.msg.featureActivated', { data: featureKey })
                        : this.$t('features.msg.featureDeactivated', { data: featureKey }),
                });
            } catch (error) {
                this.$toasted.global.toast_error({
                    message: this.$t('features.msg.toggleError'),
                });
            }
        },
        async toggleFeatureActive(item, newState) {
            const payload = {
                active: newState,
            };

            if (newState) {
                payload.activationDate = Date.now();
                payload.isActivating = true;
            } else {
                payload.activationDate = null;
                payload.isDeactivating = true;
            }
            // eslint-disable-next-line no-underscore-dangle
            await this.$rq.toggleFeatureForTeam(item.id, this.teamID, !newState);
            await this.loadTeam();
            await this.loadFeatures();
        },
        formatDate(timestamp) {
            return timestamp ? this.$options.filters.formatDate(new Date(timestamp)) : '';
        },
    },
};
</script>

  <style scoped>
  ::v-deep .action-buttons {
      justify-content: flex-start !important;
  }
  </style>
